import { DesignTemplate } from "@/api/config/chalice-types";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Button } from "antd";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import { UseStateReturn } from "fixtures/globalConstants";
import React, { useCallback, useMemo, useState } from "react";
import AddMediaButton from "./AddMediaButton";
import AddMediaModal from "./AddMediaModal/AddMediaModal";
import MediaThumbnail from "./MediaThumbnail";
import ThumbnailDragOverlay from "./ThumbnailDragOverlay";

type MediaThumbnailListProps = {
  mediaUrlsState: UseStateReturn<string[]>;
  openDesignEditor: () => void;
  templateState: UseStateReturn<DesignTemplate | undefined>;
};

const MediaThumbnailList: React.FC<MediaThumbnailListProps> = ({
  mediaUrlsState,
  templateState,
  openDesignEditor,
}) => {
  const [isAddMediaModalOpen, setIsAddMediaModalOpen] =
    useState<boolean>(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [template, setTemplate] = templateState;
  const [mediaUrls, setMediaUrls] = mediaUrlsState;
  const { hasPermission } = useUserPermissions();
  const canEditDesign = hasPermission("post_design_write");

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!canEditDesign) return;
      setActiveId(null);
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = mediaUrls.findIndex(
          (_, index) => `media-${index}` === active.id
        );
        const newIndex = mediaUrls.findIndex(
          (_, index) => `media-${index}` === over.id
        );

        if (oldIndex !== -1 && newIndex !== -1) {
          const newMediaUrls = arrayMove(mediaUrls, oldIndex, newIndex);
          setMediaUrls(newMediaUrls);

          if (template) {
            const newPages = arrayMove(template.pages, oldIndex, newIndex);
            setTemplate({ ...template, pages: newPages });
          }
        }
      }
    },
    [mediaUrls, setMediaUrls, template, setTemplate, canEditDesign]
  );

  const handleDeleteMedia = useCallback(
    (indexToDelete: number, event?: React.MouseEvent) => {
      if (!canEditDesign) return;
      event?.stopPropagation();
      const newMediaUrls = mediaUrls.filter(
        (_, index) => index !== indexToDelete
      );
      setMediaUrls(newMediaUrls);
      if (template) {
        const newPages = template.pages.filter(
          (_, index) => index !== indexToDelete
        );
        if (newPages.length > 0) {
          setTemplate({ ...template, pages: newPages });
        } else {
          setTemplate(undefined);
        }
      }
    },
    [mediaUrls, setMediaUrls, template, setTemplate, canEditDesign]
  );

  const memoizedOpenDesignEditor = useCallback(() => {
    openDesignEditor();
  }, [openDesignEditor]);

  const memoizedMediaItems = useMemo(() => {
    const isListDraggable = mediaUrls.length > 1 && canEditDesign;
    const isTemplateVideo = template?.render_design_as === "VIDEO";
    const showEditFunctionalityForItem = Boolean(template && canEditDesign);
    const showDeleteButton = canEditDesign;

    return mediaUrls.map((url, index) => {
      const id = `media-${index}`;
      return (
        <MediaThumbnail
          key={id}
          id={id}
          index={index}
          url={url}
          isDraggable={isListDraggable}
          isTemplateVideo={isTemplateVideo}
          showEditFunctionalityForItem={showEditFunctionalityForItem}
          showDeleteButton={showDeleteButton}
          handleDeleteMediaForItem={handleDeleteMedia}
          openDesignEditorForItem={memoizedOpenDesignEditor}
        />
      );
    });
  }, [
    mediaUrls,
    canEditDesign,
    template,
    handleDeleteMedia,
    memoizedOpenDesignEditor,
    template?.render_design_as,
  ]);

  const listContent = (
    <ul className="flex flex-row gap-2 p-0 flex-wrap">
      {memoizedMediaItems}
      {canEditDesign && (
        <AddMediaButton onClick={() => setIsAddMediaModalOpen(true)} />
      )}
    </ul>
  );

  return (
    <>
      <div>
        {mediaUrls.length > 1 && canEditDesign ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={mediaUrls.map((_, index) => `media-${index}`)}
              strategy={rectSortingStrategy}
            >
              {listContent}
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              <ThumbnailDragOverlay activeId={activeId} mediaUrls={mediaUrls} />
            </DragOverlay>
          </DndContext>
        ) : (
          listContent
        )}
      </div>

      {template && canEditDesign && (
        <div className="mt-4">
          <Button onClick={openDesignEditor} type="default" size="small">
            Edit Design
          </Button>
        </div>
      )}

      <AddMediaModal
        onClose={() => setIsAddMediaModalOpen(false)}
        isOpen={isAddMediaModalOpen}
        mediaUrlsState={mediaUrlsState}
        templateState={templateState}
      />
    </>
  );
};

export default MediaThumbnailList;
